import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TextField,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useGetHistory } from '../../api/queries';
import { MOVEMENT_LABEL, STOCK_STATE_LABEL } from '../../utils/mapping';
import { formatDate } from '../../utils/formatters';
import { fr } from '../../i18n/fr';
import { formatPaginationLabel } from '../../utils/pagination';

interface Props {
  productId?: string;
  productName?: string | null;
  storeId?: string;
  storeName?: string;
  storeNamesById?: Record<string, string>;
}

const resolveStockStateLabel = (state: string) =>
  state === 'all'
    ? 'Tous les états'
    : STOCK_STATE_LABEL[state as keyof typeof STOCK_STATE_LABEL] || state || '—';

const HistoryTable: React.FC<Props> = ({
  productId,
  productName,
  storeId,
  storeName,
  storeNamesById,
}) => {
  const { data, isPending, error } = useGetHistory(productId, storeId);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [stockStateFilter, setStockStateFilter] = React.useState<'all' | string>('all');
  const resolvedData = React.useMemo(() => data ?? [], [data]);
  const availableStockStates = React.useMemo(() => {
    const unique = Array.from(
      new Set(
        resolvedData
          .map((movement) => movement.stock_state)
          .filter(Boolean)
      )
    );
    return ['all', ...unique];
  }, [resolvedData]);
  const filteredData = React.useMemo(() => {
    if (stockStateFilter === 'all') {
      return resolvedData;
    }
    return resolvedData.filter((movement) => movement.stock_state === stockStateFilter);
  }, [resolvedData, stockStateFilter]);

  React.useEffect(() => {
    setPage(0);
  }, [productId, storeId, stockStateFilter]);

  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredData.length / rowsPerPage) - 1
    );
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredData.length, rowsPerPage, page]);

  const paginatedData = React.useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (
    _event: unknown,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const resolveStockStateLabel = React.useCallback(
    (state: string) =>
      state === 'all'
        ? 'Tous les états'
        : STOCK_STATE_LABEL[state as keyof typeof STOCK_STATE_LABEL] || state || '—',
    []
  );

  if (!productId) return null;
  if (isPending)
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  if (error)
    return <Alert severity="error">{(error as Error).message || fr.error}</Alert>;

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        borderRadius: 3,
        background: (theme) =>
          `linear-gradient(160deg, ${alpha(theme.palette.secondary.light, 0.12)}, ${alpha(
            theme.palette.background.paper,
            0.9
          )})`,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        mb={1}
      >
        <Box>
          <Typography variant="h6" color="secondary" gutterBottom>
            {fr.historyTitle(productName ?? productId)}
          </Typography>
          {storeName && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {storeName}
            </Typography>
          )}
        </Box>
        <TextField
          select
          size="small"
          label={fr.stockState}
          value={stockStateFilter}
          onChange={(event) => setStockStateFilter(event.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 200 } }}
        >
          {availableStockStates.map((state) => (
            <MenuItem key={state} value={state}>
              {resolveStockStateLabel(state)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <TableContainer>
        <Table aria-label="history-table">
          <TableHead>
            <TableRow>
              <TableCell>Date/Heure</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Magasin</TableCell>
              <TableCell>{fr.stockState}</TableCell>
              <TableCell align="right">{fr.quantity}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length ? (
              paginatedData.map((movement) => {
                const resolvedStore = movement.store_id
                  ? storeNamesById?.[movement.store_id] ?? movement.store_id
                  : '—';
                return (
                  <TableRow key={movement.id} hover>
                    <TableCell>{formatDate(movement.ts)}</TableCell>
                    <TableCell>
                      {MOVEMENT_LABEL[movement.type] || movement.type}
                    </TableCell>
                    <TableCell>{resolvedStore}</TableCell>
                    <TableCell>{STOCK_STATE_LABEL[movement.stock_state]}</TableCell>
                    <TableCell align="right">{movement.quantity}</TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {fr.noResults}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage={fr.productsPerPage}
        labelDisplayedRows={formatPaginationLabel}
      />
    </Paper>
  );
};

export default HistoryTable;
